import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { Panel, ActionButton, TextField, LocalizedField } from "./_shared";
import { Save, Bell } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

export default function SettingsManager() {
  const [s, setS] = useState(null);
  const { refresh } = useSettings();

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setS(data || {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.patch("/admin/settings", s);
      toast.success("Impostazioni salvate");
      refresh();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (!s) return null;

  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-editorial italic text-4xl text-ink-900">Impostazioni</h1>
          <p className="text-xs text-ink-500 mt-1">Personalizzazione globale della piattaforma</p>
        </div>
        <ActionButton onClick={save} testid="settings-save"><Save className="w-3.5 h-3.5" /> Salva</ActionButton>
      </div>

      <Panel title="Identità">
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Nome struttura" value={s.site_name} onChange={(v) => set("site_name", v)} testid="settings-site-name"/>
          <TextField label="Etichetta località" value={s.location_label} onChange={(v) => set("location_label", v)} hint="es. SIRMIONE · LAGO DI GARDA"/>
          <div className="md:col-span-2"><LocalizedField label="Titolo di benvenuto (H1)" value={s.tagline} onChange={(v) => set("tagline", v)} testid="settings-tagline" /></div>
          <div className="md:col-span-2"><LocalizedField label="Testo di benvenuto" value={s.welcome_text} onChange={(v) => set("welcome_text", v)} testid="settings-welcome-text" multiline /></div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink-500">Lingua predefinita</label>
            <select value={s.default_language} onChange={(e) => set("default_language", e.target.value)} className="mt-1 w-full h-10 bg-white border border-ink-200/60 rounded-md px-3">
              <option value="it">Italiano</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          <TextField label="Giorni sessione default" type="number" value={s.default_session_days} onChange={(v) => set("default_session_days", v)} />
        </div>
      </Panel>

      <Panel title="Posizione della struttura">
        <p className="text-xs text-ink-500 mb-3">
          Coordinate esatte usate per ogni link "Indicazioni" e per il marker "Sei qui" sulla mappa.
          Puoi trovarle facendo click destro sulla proprietà in Google Maps e copiando le coordinate.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField
            label="Latitudine proprietà"
            type="number"
            value={s.property_lat ?? ""}
            onChange={(v) => set("property_lat", v === "" ? null : parseFloat(v))}
            hint="Es. 45.4677778"
            testid="settings-property-lat"
          />
          <TextField
            label="Longitudine proprietà"
            type="number"
            value={s.property_lng ?? ""}
            onChange={(v) => set("property_lng", v === "" ? null : parseFloat(v))}
            hint="Es. 10.6070538"
            testid="settings-property-lng"
          />
        </div>
        <div className="mt-3">
          <TextField
            label="Link Google Maps completo (consigliato)"
            value={s.property_maps_url ?? ""}
            onChange={(v) => set("property_maps_url", v)}
            hint="Incolla il link corto della vostra scheda business (es. https://maps.app.goo.gl/xxx). Se compilato, viene usato al posto delle coordinate e mostra anche il nome della struttura nel pannello Google Maps."
            testid="settings-property-maps-url"
          />
        </div>
      </Panel>

      <Panel title="Meteo">
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Latitudine" type="number" value={s.weather_lat} onChange={(v) => set("weather_lat", v)} hint="Aperto: Open-Meteo, senza chiave API"/>
          <TextField label="Longitudine" type="number" value={s.weather_lng} onChange={(v) => set("weather_lng", v)} />
        </div>
      </Panel>

      <Panel title="Contatti">
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Telefono" value={s.contact_phone} onChange={(v) => set("contact_phone", v)} />
          <TextField
            label="WhatsApp"
            value={s.contact_whatsapp || ""}
            onChange={(v) => set("contact_whatsapp", v)}
            hint="Lascia vuoto per usare lo stesso numero del telefono"
          />
          <TextField label="Email" value={s.contact_email} onChange={(v) => set("contact_email", v)} />
          <TextField label="Indirizzo" value={s.contact_address} onChange={(v) => set("contact_address", v)} />
          <TextField label="Sito web" value={s.contact_website} onChange={(v) => set("contact_website", v)} />
          <TextField label="Facebook" value={s.social_facebook} onChange={(v) => set("social_facebook", v)} />
          <TextField label="Instagram" value={s.social_instagram} onChange={(v) => set("social_instagram", v)} />
        </div>
      </Panel>

      <Panel title={<span className="flex items-center gap-2"><Bell className="w-4 h-4" /> Notifiche push (predisposto)</span>}>
        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="VAPID public key" value={s.vapid_public_key} onChange={(v) => set("vapid_public_key", v)} hint="Genera su web-push (attualmente disattivate)"/>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={!!s.push_enabled} onChange={(e) => set("push_enabled", e.target.checked)} />
            <span className="text-sm">Abilita richiesta permessi push</span>
          </label>
        </div>
      </Panel>
    </div>
  );
}
